import type { ReactNode } from 'react';

import { Box, forwardRef, Popover, PopoverContent, PopoverProps, PopoverTrigger, Portal } from '@bonusly/design-system';
import { Recipient } from '@bonusly/recipient-entities';

import { useIsTouchscreen } from '../../../../hooks/use-is-touchscreen';
import { ErrorBoundary } from '../../../error/error-boundary';
import { GroupPopoverContent } from './group-popover-content';

export interface GroupPopoverProps extends PopoverProps {
	children: ReactNode;
	group: Recipient;
	isDisabled?: boolean;
	isExpandable?: boolean;
	onExpand?: (users: Recipient[]) => void;
}

const GroupPopover = forwardRef((props: GroupPopoverProps, ref) => {
	// trigger and isOpen are intentionally discarded from rest so that callers cannot override
	// the touchscreen-aware trigger or the isDisabled-derived open state via prop spread.
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { children, group, isDisabled, isExpandable, onExpand, trigger: _trigger, isOpen: _isOpen, ...rest } = props;
	const isTouchscreen = useIsTouchscreen();
	const trigger: PopoverProps['trigger'] = isTouchscreen ? 'click' : 'hover';
	const isPopoverDisabled = isDisabled ? false : undefined;

	return (
		<ErrorBoundary>
			<Box display="inline-block" ref={ref}>
				<Popover isLazy trigger={trigger} isOpen={isPopoverDisabled} {...rest}>
					<PopoverTrigger>{children}</PopoverTrigger>

					<Portal>
						<Box
							sx={{
								'& .chakra-popover__popper': {
									zIndex: 'popover',
								},
							}}
						>
							<PopoverContent borderColor="bg.subtle" boxShadow="xl">
								<GroupPopoverContent group={group} isExpandable={isExpandable} onExpand={onExpand} />
							</PopoverContent>
						</Box>
					</Portal>
				</Popover>
			</Box>
		</ErrorBoundary>
	);
});

GroupPopover.displayName = 'GroupPopover';

export { GroupPopover };
